import { Add, Delete, Edit, Search } from "@mui/icons-material";
import {
    Box,
    Breadcrumbs,
    Button,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatDocument, formatPhone, formatUuid, formatZipCode } from "../../helpers/formatters"; // Importando o SweetAlert2

function ListCustomersPage() {
    const [customers, setCustomers] = useState([]);
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchCustomers = useCallback(() => {
        setIsLoading(true); // Indica que o carregamento está em andamento
        api.get(`customers?page=${currentPage}&limit=${itemsPerPage}&order_by=name&order=1&q=${searchTerm}`)
            .then(response => {
                setCustomers(response.data.customers);
                setTotalPages(response.data.total_pages);
            })
            .catch(error => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false); // Finaliza o carregamento
            });
    }, [currentPage, itemsPerPage, searchTerm]);

    useListFetch(fetchCustomers, [currentPage, itemsPerPage, appliedSearchTerm, fetchCustomers]);

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(true);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleCreateCustomer = () => {
        navigate('/admin/register-customer');  // Redireciona para a página de criação de cliente
    };

    const handleEditCustomer = (id) => {
        navigate(`/admin/edit-customer/${id}`);  // Redireciona para a página de edição
    };

    const handleDeleteCustomer = (customerId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este cliente será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
        }).then((result) => {
            if (result.isConfirmed) {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`customers/${customerId}`)
                        .then(response => {
                            // Atualiza o estado após remoção
                            setCustomers(customers.filter((customer) => customer.uuid !== customerId));
                            Swal.fire('Removido!', 'Cliente removido com sucesso.', 'success');
                            setLoadingDelete(false);
                        })
                        .catch(error => {
                            console.error('Erro ao remover o cliente:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover o cliente.', 'error');
                            setLoadingDelete(false);
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Clientes</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Clientes
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                {/* Botão de venda */}
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateCustomer}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Cliente
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>E-mail</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Endereço</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(customers) && customers.length > 0 && customers.map((customer) => (
                            <TableRow key={customer.uuid}>
                                <TableCell>{formatUuid(customer.uuid)}</TableCell>
                                <TableCell>{customer.name}</TableCell>
                                <TableCell>{formatPhone(customer.phone)}</TableCell>
                                <TableCell>{customer.email}</TableCell>
                                <TableCell>{formatDocument(customer.document)}</TableCell>
                                <TableCell>
                                    {customer.address}&nbsp;
                                    {customer.address_number}&nbsp;
                                    {customer.address_complement}&nbsp;
                                    <br />
                                    {customer.neighborhood}&nbsp;
                                    {customer.city}&nbsp;
                                    {customer.state}&nbsp;
                                    {formatZipCode(customer.zip_code)}&nbsp;
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditCustomer(customer.uuid)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteCustomer(customer.uuid)}
                                        aria-label="remover cliente"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListCustomersPage;
